import React, { useState } from 'react';
import { apiFetch } from '../../api/api';
import './AttachmentForm.css';

export default function AttachmentForm({
  isVisible,
  wells = [],
  dailyReports = [],
  onClose,
  onSuccess,
  title = "Upload Attachment"
}) {
  const [file, setFile] = useState(null);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [wellId, setWellId] = useState('');
  const [reportId, setReportId] = useState('');
  const [bitId, setBitId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    console.log('📁 File selected in AttachmentForm:', selectedFile?.name, selectedFile?.type, selectedFile?.size);
    setFile(selectedFile);
    if (selectedFile && !subject) {
      setSubject(selectedFile.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    if (!subject.trim()) {
      setError('Subject is required.');
      return;
    }
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    if (!wellId && !reportId && !bitId) {
      setError('Please link the attachment to a Well, Report, or Bit ID.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      console.log('📤 Creating FormData for attachment upload...');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subject', subject.trim());
      formData.append('description', description.trim());
      
      if (wellId) formData.append('wellId', wellId);
      if (reportId) formData.append('reportId', reportId);
      if (bitId) formData.append('bitId', bitId);

      console.log('📡 Sending upload request to /api/attachments...');
      const response = await apiFetch('/api/attachments', {
        method: 'POST',
        body: formData
      });

      console.log('📥 Upload response received:', response);

      if (response && (response.attachment || response.success || response.message)) {
        const message = response.message || 'File uploaded successfully!';
        console.log('✅ Upload successful:', message);
        
        // Reset form
        setFile(null);
        setSubject('');
        setDescription('');
        setWellId('');
        setReportId('');
        setBitId('');
        
        // Clear file input
        const fileInput = document.getElementById('attachment-file-input');
        if (fileInput) fileInput.value = '';
        
        if (onSuccess) {
          onSuccess(message);
        }
      } else {
        throw new Error(response?.error || 'Upload failed with unexpected response');
      }
    } catch (err) {
      console.error('❌ Upload error:', err);
      setError(err.message || 'Failed to upload attachment');
    } finally {
      setIsSaving(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isVisible) return null;

  return (
    <div className="attachment-form-overlay">
      <div className="attachment-form">
        <div className="attachment-header">
          <h3>{title}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="attachment-content">
          <div className="attachment-fields">
            <div className="form-field">
              <label className="form-label">
                Select File *
              </label>
              <input
                type="file"
                id="attachment-file-input"
                onChange={handleFileSelect}
                className="form-input"
                required
              />
              {file && (
                <div className="file-info">
                  <div className="file-icon">📎</div>
                  <div className="file-details">
                    <div className="file-name">{file.name}</div>
                    <div className="file-size">{formatFileSize(file.size)}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="form-field">
              <label className="form-label">
                Subject *
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject..."
                className="form-input"
                required
              />
            </div>

            <div className="form-field">
              <label className="form-label">
                Description *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description..."
                className="form-textarea"
                rows={3}
                required
              />
            </div>

            <div className="form-field">
              <label className="form-label">
                Link to Well
              </label>
              <select
                value={wellId}
                onChange={(e) => setWellId(e.target.value)}
                className="form-select"
              >
                <option value="">Select Well (Optional)</option>
                {wells.map(well => (
                  <option key={well.WellID} value={well.WellID}>
                    {well.WellName} ({well.WellID})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label className="form-label">
                Link to Daily Report
              </label>
              <select
                value={reportId}
                onChange={(e) => setReportId(e.target.value)}
                className="form-select"
              >
                <option value="">Select Report (Optional)</option>
                {dailyReports.map(report => (
                  <option key={report.ReportID} value={report.ReportID}>
                    Report {report.ReportID} - {report.ReportDate ? new Date(report.ReportDate).toLocaleDateString() : 'No Date'}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label className="form-label">
                Bit ID (Optional)
              </label>
              <input
                type="number"
                value={bitId}
                onChange={(e) => setBitId(e.target.value)}
                placeholder="Enter Bit ID..."
                className="form-input"
              />
            </div>

            <div className="form-note">
              <small>* At least one link (Well, Report, or Bit ID) is required</small>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="attachment-actions">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={isSaving}
            >
              {isSaving ? 'Uploading...' : 'Upload Attachment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 